// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Graphique en barres groupées à DEUX échelles verticales indépendantes.
//
// Écrit en SVG plutôt qu'avec une bibliothèque : le front n'embarque aucune
// dépendance de graphiques (cf package.json) et en ajouter une pour un seul
// écran alourdirait l'image frontend sans bénéfice (décision Marc, 28/07/2026).
//
// Les deux séries ont leur propre axe : à gauche des comptages qui se lisent en
// unités (des analyses), à droite des volumes qui se lisent en centaines (des
// pages OCR). Sur un axe commun, la série de gauche serait écrasée à plat dès
// qu'un rapport scanné passe.
//
// Aucune chaîne de texte n'est écrite ici : tous les libellés sont injectés par
// l'appelant, déjà traduits (règle i18n stricte).
import React from 'react';

const M = { haut: 12, droite: 46, bas: 32, gauche: 46 }; // marges internes
const LARGEUR = 820;
const HAUTEUR = 250;
const NB_GRADUATIONS = 4;

// Plafond d'axe « rond » et divisible par le nombre de graduations, pour ne
// jamais afficher 3,75 analyses sur une graduation. Pas minimal de 1 : les deux
// séries sont des comptages entiers.
const plafondAxe = (valeurMax) => {
  if (!valeurMax || valeurMax <= 0) return NB_GRADUATIONS;
  const brut = valeurMax / NB_GRADUATIONS;
  const magnitude = Math.pow(10, Math.floor(Math.log10(brut)));
  const normalise = brut / magnitude;
  const pasArrondi = normalise <= 1 ? 1 : normalise <= 2 ? 2 : normalise <= 5 ? 5 : 10;
  return Math.max(1, Math.round(pasArrondi * magnitude)) * NB_GRADUATIONS;
};

const BarChartDouble = ({
  points = [],                 // [{ cle, gauche, droite }]
  labelX = (cle) => String(cle),
  labelSerieGauche = '',
  labelSerieDroite = '',
  couleurGauche = 'var(--blue)',
  couleurDroite = 'var(--gold)',
  titrePoint,                  // (point) => string, infobulle native au survol
  ariaLabel = '',
}) => {
  const zoneL = LARGEUR - M.gauche - M.droite;
  const zoneH = HAUTEUR - M.haut - M.bas;
  const baseY = M.haut + zoneH;

  const maxGauche = plafondAxe(Math.max(0, ...points.map(p => p.gauche || 0)));
  const maxDroite = plafondAxe(Math.max(0, ...points.map(p => p.droite || 0)));

  const pasCase = points.length ? zoneL / points.length : zoneL;
  // Deux barres par case, plus une respiration de part et d'autre.
  const largeurBarre = Math.max(2, Math.min(14, (pasCase - 4) / 2));

  // Au-delà d'une quinzaine de cases (un mois de 31 jours), les libellés se
  // chevauchent : on n'en garde qu'un sur N, les barres restent toutes là.
  const pasLabel = points.length > 24 ? 3 : points.length > 15 ? 2 : 1;

  const hauteurBarre = (valeur, plafond) => (!valeur || valeur <= 0 ? 0 : (valeur / plafond) * zoneH);

  const legende = (couleur, texte) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, background: couleur, display: 'inline-block' }} />
      {texte}
    </span>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 8 }}>
        {legende(couleurGauche, labelSerieGauche)}
        {legende(couleurDroite, labelSerieDroite)}
      </div>
      <svg
        viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`}
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={ariaLabel}
      >
        {/* Grille + graduations des deux axes */}
        {Array.from({ length: NB_GRADUATIONS + 1 }, (_, i) => {
          const y = baseY - (i / NB_GRADUATIONS) * zoneH;
          return (
            <g key={`grad-${i}`}>
              <line x1={M.gauche} y1={y} x2={M.gauche + zoneL} y2={y}
                    stroke="var(--gray)" strokeWidth="1" />
              <text x={M.gauche - 6} y={y + 3} textAnchor="end"
                    fontSize="10" fill={couleurGauche}>
                {Math.round((i / NB_GRADUATIONS) * maxGauche)}
              </text>
              <text x={M.gauche + zoneL + 6} y={y + 3} textAnchor="start"
                    fontSize="10" fill="var(--text-muted)">
                {Math.round((i / NB_GRADUATIONS) * maxDroite)}
              </text>
            </g>
          );
        })}

        {points.map((point, index) => {
          const centre = M.gauche + index * pasCase + pasCase / 2;
          const hG = hauteurBarre(point.gauche, maxGauche);
          const hD = hauteurBarre(point.droite, maxDroite);
          return (
            <g key={point.cle}>
              {titrePoint && <title>{titrePoint(point)}</title>}
              {/* Bande de survol : rend l'infobulle atteignable même sur une case vide */}
              <rect x={centre - pasCase / 2} y={M.haut} width={pasCase} height={zoneH} fill="transparent" />
              <rect x={centre - largeurBarre - 1} y={baseY - hG}
                    width={largeurBarre} height={hG} fill={couleurGauche} rx="1" />
              <rect x={centre + 1} y={baseY - hD}
                    width={largeurBarre} height={hD} fill={couleurDroite} rx="1" />
              {index % pasLabel === 0 && (
                <text x={centre} y={baseY + 14} textAnchor="middle"
                      fontSize="10" fill="var(--text-muted)">
                  {labelX(point.cle)}
                </text>
              )}
            </g>
          );
        })}

        {/* Ligne de base par-dessus les barres, pour un pied net */}
        <line x1={M.gauche} y1={baseY} x2={M.gauche + zoneL} y2={baseY}
              stroke="var(--gray)" strokeWidth="1.5" />
      </svg>
    </div>
  );
};

export default BarChartDouble;
